/**
 * What a failed request tells the error card: the status, and the server's own
 * one-line reason when it gave one.
 */
export function failureOf(result: { status: number; body: unknown } | undefined): {
  status: number | undefined;
  detail: string | undefined;
} {
  if (result === undefined) return { status: undefined, detail: undefined };
  const body = result.body as { message?: unknown } | null;
  const message = typeof body?.message === 'string' ? body.message : undefined;
  return { status: result.status, detail: message };
}

/** A response that was not the one a query needed, carried to the error card. */
export class ApiFailure extends Error {
  constructor(
    readonly status: number | undefined,
    readonly detail: string | undefined,
  ) {
    super(detail ?? `API answered ${status ?? 'nothing'}`);
  }
}

/**
 * The body of the expected response, or an ApiFailure thrown for React Query to
 * hold — so a screen reads `data` or `error` and never inspects statuses.
 */
export function expectStatus<R extends { status: number; body: unknown }, S extends R['status']>(
  result: R,
  status: S,
): Extract<R, { status: S }>['body'] {
  if (result.status === status) return result.body;
  const { detail } = failureOf(result);
  throw new ApiFailure(result.status, detail);
}

/** What the error card needs from whatever a query threw: a fetch that never landed has no status. */
export function failureFrom(error: unknown): {
  status: number | undefined;
  detail: string | undefined;
} {
  return error instanceof ApiFailure
    ? { status: error.status, detail: error.detail }
    : { status: undefined, detail: undefined };
}
